import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import ScheduleRangeModal from '../components/ScheduleRangeModal';
import ScheduleAvailabilityBlock from '../components/ScheduleAvailabilityBlock';
import {
  HOURS_IN_DAY,
  formatHour,
  hoursToSegments,
  getHourFromPointer,
  normalizeRange,
  segmentStyle,
  formatUTCDate,
  parseUTCDate,
  isSameUTCDay,
  getMondayOfWeekUTC,
  formatUTCDateTime,
} from '../utils/scheduleUtils';
import './Schedule.css';

const DAY_NAMES = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];
const DAY_NAMES_FULL = [
  'Понедельник', 'Вторник', 'Среда', 'Четверг', 'Пятница', 'Суббота', 'Воскресенье',
];

const getStatusColor = (status) => {
  switch (status) {
    case 'completed':
      return '#10b981';
    case 'cancelled':
      return '#ef4444';
    case 'scheduled':
      return '#6366f1';
    default:
      return '#6b7280';
  }
};

const getStatusText = (status) => {
  switch (status) {
    case 'completed':
      return 'Состоялся';
    case 'cancelled':
      return 'Отменен';
    case 'scheduled':
      return 'Запланирован';
    default:
      return status;
  }
};

const isSameSegment = (a, b) =>
  a && b && a.startHour === b.startHour && a.endHour === b.endHour;

const ScheduleDayColumn = ({
  dayData,
  dayIndex,
  isToday,
  drag,
  onStartDrag,
  onGridRef,
  onLessonClick,
  onDeleteRequest,
}) => {
  const gridRef = useRef(null);

  useEffect(() => {
    if (gridRef.current && onGridRef) {
      onGridRef(dayIndex, gridRef.current);
    }
  }, [dayIndex, onGridRef]);
  const availableHours = dayData.available_hours || [];
  const segments = hoursToSegments(availableHours);
  const activeLessons = (dayData.lessons || []).filter((l) => l.status !== 'cancelled');

  const handleGridMouseDown = (e) => {
    if (e.button !== 0) return;
    if (e.target.closest('.schedule-lesson-block')) return;
    if (e.target.closest('.schedule-availability-block')) return;
    if (!gridRef.current) return;

    const hour = getHourFromPointer(e.clientY, gridRef.current);
    onStartDrag({
      dayIndex,
      anchorHour: hour,
      startHour: hour,
      endHour: hour + 1,
      mode: 'add',
      existingSegment: null,
      date: dayData.date,
      dayOfWeek: dayData.day_of_week,
    });
  };

  const handleMoveStart = (e, segment) => {
    e.stopPropagation();
    if (e.button !== 0) return;
    if (!gridRef.current) return;
    const pointerHour = getHourFromPointer(e.clientY, gridRef.current);
    onStartDrag({
      dayIndex,
      startHour: segment.startHour,
      endHour: segment.endHour,
      mode: 'move',
      existingSegment: { ...segment },
      grabOffsetHour: pointerHour - segment.startHour,
      date: dayData.date,
      dayOfWeek: dayData.day_of_week,
    });
  };

  const handleResizeStart = (e, segment, resizeMode) => {
    e.stopPropagation();
    if (e.button !== 0) return;
    onStartDrag({
      dayIndex,
      startHour: segment.startHour,
      endHour: segment.endHour,
      mode: resizeMode,
      existingSegment: { ...segment },
      date: dayData.date,
      dayOfWeek: dayData.day_of_week,
    });
  };

  const showPreview =
    drag &&
    drag.dayIndex === dayIndex &&
    drag.startHour !== undefined &&
    drag.endHour !== undefined &&
    drag.mode !== 'add';

  const previewRange = showPreview
    ? { startHour: drag.startHour, endHour: drag.endHour }
    : null;

  const showAddPreview =
    drag &&
    drag.dayIndex === dayIndex &&
    drag.mode === 'add';

  const addPreviewRange = showAddPreview
    ? { startHour: drag.startHour, endHour: drag.endHour }
    : null;

  return (
    <div className="schedule-day-column">
      <div className={`schedule-day-header ${isToday ? 'today' : ''}`}>
        <div className="schedule-day-number">{dayData.day}</div>
        <div className="schedule-day-name">{DAY_NAMES[dayData.day_of_week]}</div>
      </div>

      <div
        ref={gridRef}
        className="schedule-day-grid"
        onMouseDown={handleGridMouseDown}
        role="presentation"
      >
        {Array.from({ length: HOURS_IN_DAY }, (_, hour) => (
          <div
            key={hour}
            className="schedule-grid-line"
            style={{ top: `${(hour / HOURS_IN_DAY) * 100}%` }}
          />
        ))}

        {segments.map((segment) => (
          <ScheduleAvailabilityBlock
            key={`${segment.startHour}-${segment.endHour}`}
            segment={segment}
            isDragging={
              drag?.dayIndex === dayIndex && isSameSegment(drag.existingSegment, segment)
            }
            onMoveStart={handleMoveStart}
            onResizeStart={handleResizeStart}
            onDeleteRequest={(seg) =>
              onDeleteRequest({
                dayIndex,
                dayOfWeek: dayData.day_of_week,
                date: dayData.date,
                segment: seg,
              })
            }
          />
        ))}

        {previewRange && (
          <div
            className="schedule-drag-preview schedule-drag-preview--active"
            style={segmentStyle(previewRange.startHour, previewRange.endHour)}
          />
        )}

        {addPreviewRange && (
          <div
            className="schedule-drag-preview"
            style={segmentStyle(addPreviewRange.startHour, addPreviewRange.endHour)}
          />
        )}

        {activeLessons.map((lesson) => {
          const endHour = lesson.hour + 1;
          return (
            <div
              key={lesson.id}
              className="schedule-lesson-block"
              style={{
                ...segmentStyle(lesson.hour, endHour),
                backgroundColor: getStatusColor(lesson.status),
              }}
              title={`${lesson.student_name} — ${getStatusText(lesson.status)}`}
              onMouseDown={(e) => e.stopPropagation()}
              onClick={() => onLessonClick({ ...lesson, date: dayData.date })}
            >
              <span className="schedule-lesson-name">{lesson.student_name}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const Schedule = () => {
  const { user } = useAuth();
  const [weekStart, setWeekStart] = useState(() => getMondayOfWeekUTC());
  const [calendarData, setCalendarData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [drag, setDrag] = useState(null);
  const [pendingRange, setPendingRange] = useState(null);
  const [saving, setSaving] = useState(false);
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [showLessonModal, setShowLessonModal] = useState(false);
  const gridRefs = useRef({});

  const fetchCalendar = useCallback(async () => {
    setLoading(true);
    try {
      const weekStartStr = formatUTCDate(weekStart);
      const response = await api.get(`/lessons/schedule/calendar/?week_start=${weekStartStr}`);
      setCalendarData(response.data);
    } catch (error) {
      console.error('Ошибка загрузки календаря:', error);
    } finally {
      setLoading(false);
    }
  }, [weekStart]);

  useEffect(() => {
    if (user && user.user_type === 'teacher') {
      fetchCalendar();
    }
  }, [user, fetchCalendar]);

  useEffect(() => {
    if (!drag) return undefined;

    const handleMouseMove = (e) => {
      const col = gridRefs.current[drag.dayIndex];
      if (!col) return;
      const hour = getHourFromPointer(e.clientY, col);
      setDrag((prev) => {
        if (!prev) return prev;

        if (prev.mode === 'move') {
          const duration = prev.endHour - prev.startHour;
          const grabOffset = prev.grabOffsetHour ?? 0;
          const newStart = Math.max(
            0,
            Math.min(HOURS_IN_DAY - duration, hour - grabOffset),
          );
          return {
            ...prev,
            startHour: newStart,
            endHour: newStart + duration,
          };
        }

        if (prev.mode === 'resize-start') {
          const newStart = Math.max(0, Math.min(hour, prev.endHour - 1));
          return { ...prev, startHour: newStart };
        }

        if (prev.mode === 'resize-end') {
          const newEnd = Math.min(HOURS_IN_DAY, Math.max(hour + 1, prev.startHour + 1));
          return { ...prev, endHour: newEnd };
        }

        if (prev.mode === 'add') {
          const anchor = prev.anchorHour ?? prev.startHour;
          const start = Math.min(anchor, hour);
          const end = Math.max(anchor, hour) + 1;
          return { ...prev, startHour: start, endHour: end };
        }

        return prev;
      });
    };

    const handleMouseUp = () => {
      setDrag((prev) => {
        if (!prev) return null;
        const range = normalizeRange(prev.startHour, prev.endHour - 1);
        if (range.endHour <= range.startHour) return null;

        if (prev.mode === 'add') {
          setPendingRange({
            mode: 'add',
            dayIndex: prev.dayIndex,
            dayOfWeek: prev.dayOfWeek,
            date: prev.date,
            startHour: range.startHour,
            endHour: range.endHour,
          });
          return null;
        }

        const orig = prev.existingSegment;
        if (
          orig &&
          range.startHour === orig.startHour &&
          range.endHour === orig.endHour
        ) {
          return null;
        }

        setPendingRange({
          mode: 'modify',
          dayIndex: prev.dayIndex,
          dayOfWeek: prev.dayOfWeek,
          date: prev.date,
          startHour: range.startHour,
          endHour: range.endHour,
          oldStartHour: orig?.startHour,
          oldEndHour: orig?.endHour,
        });
        return null;
      });
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [drag]);

  const changeWeek = (delta) => {
    const newWeekStart = new Date(weekStart);
    newWeekStart.setUTCDate(newWeekStart.getUTCDate() + delta * 7);
    setWeekStart(newWeekStart);
  };

  const goToCurrentWeek = () => {
    setWeekStart(getMondayOfWeekUTC());
  };

  const getWeekRange = () => {
    if (!calendarData) return '';
    const start = parseUTCDate(calendarData.week_start);
    const end = parseUTCDate(calendarData.week_end);
    const startDay = start.getUTCDate();
    const endDay = end.getUTCDate();
    const monthName = calendarData.month_name;
    const year = calendarData.year;

    if (start.getUTCMonth() === end.getUTCMonth()) {
      return `${startDay} - ${endDay} ${monthName} ${year}`;
    }
    const endMonthNames = [
      'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
      'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь',
    ];
    return `${startDay} ${monthName} - ${endDay} ${endMonthNames[end.getUTCMonth()]} ${year}`;
  };

  const handleDeleteRequest = ({ dayIndex, dayOfWeek, date, segment }) => {
    setPendingRange({
      mode: 'delete',
      dayIndex,
      dayOfWeek,
      date,
      startHour: segment.startHour,
      endHour: segment.endHour,
    });
  };

  const handleConfirmRange = async (scope) => {
    if (!pendingRange) return;
    setSaving(true);
    try {
      const actionMap = { add: 'add', modify: 'update', delete: 'remove' };
      const payload = {
        scope,
        action: actionMap[pendingRange.mode] || 'add',
        day_of_week: pendingRange.dayOfWeek,
        start_hour: pendingRange.startHour,
        end_hour: pendingRange.endHour,
        date: pendingRange.date.split('T')[0],
      };
      if (pendingRange.mode === 'modify' && pendingRange.oldStartHour != null) {
        payload.old_start_hour = pendingRange.oldStartHour;
        payload.old_end_hour = pendingRange.oldEndHour;
      }
      await api.post('/lessons/schedule/apply-range/', payload);
      setPendingRange(null);
      await fetchCalendar();
    } catch (error) {
      console.error('Ошибка сохранения расписания:', error);
      alert(error.response?.data?.error || 'Не удалось сохранить расписание');
    } finally {
      setSaving(false);
    }
  };

  const formatDateLabel = (dateStr) => {
    const d = parseUTCDate(dateStr);
    return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', timeZone: 'UTC' });
  };

  if (loading && !calendarData) {
    return <div className="schedule-loading">Загрузка...</div>;
  }

  if (!calendarData) {
    return <div className="schedule-empty">Нет данных</div>;
  }

  return (
    <div className="schedule-container">
      <div className="schedule-header">
        <h2 className="schedule-title">Расписание</h2>
        <div className="schedule-controls">
          <button type="button" className="schedule-nav-btn" onClick={() => changeWeek(-1)} aria-label="Предыдущая неделя">
            ←
          </button>
          <span className="schedule-week">{getWeekRange()}</span>
          <button type="button" className="schedule-nav-btn" onClick={() => changeWeek(1)} aria-label="Следующая неделя">
            →
          </button>
          <button type="button" className="schedule-today-btn" onClick={goToCurrentWeek}>
            Сегодня
          </button>
        </div>
      </div>

      <p className="schedule-hint">
        Все время указано в UTC. Потяните по пустому месту — новый интервал. Наведите на блок — удаление. Потяните края — изменить длительность, центр — перенести.
      </p>

      <div className="schedule-calendar">
        <div className="schedule-time-column">
          <div className="schedule-time-header" />
          <div className="schedule-time-grid">
            {Array.from({ length: HOURS_IN_DAY }, (_, hour) => (
              <div
                key={hour}
                className="schedule-time-cell"
                style={{ top: `${(hour / HOURS_IN_DAY) * 100}%` }}
              >
                {formatHour(hour)}
              </div>
            ))}
          </div>
        </div>

        <div className="schedule-days-container">
          {calendarData.calendar.map((dayData, dayIndex) => {
            const date = parseUTCDate(dayData.date);
            const isToday = isSameUTCDay(date, new Date());
            return (
              <ScheduleDayColumn
                key={dayData.date}
                dayData={dayData}
                dayIndex={dayIndex}
                isToday={isToday}
                drag={drag}
                onStartDrag={setDrag}
                onGridRef={(idx, el) => {
                  gridRefs.current[idx] = el;
                }}
                onLessonClick={(lesson) => {
                  setSelectedLesson(lesson);
                  setShowLessonModal(true);
                }}
                onDeleteRequest={handleDeleteRequest}
              />
            );
          })}
        </div>
      </div>

      {pendingRange && (
        <ScheduleRangeModal
          mode={pendingRange.mode}
          dayLabel={DAY_NAMES_FULL[pendingRange.dayOfWeek]}
          dateLabel={formatDateLabel(pendingRange.date)}
          startHour={pendingRange.startHour}
          endHour={pendingRange.endHour}
          saving={saving}
          onClose={() => setPendingRange(null)}
          onConfirm={handleConfirmRange}
        />
      )}

      {showLessonModal && selectedLesson && (
        <LessonModal
          lesson={selectedLesson}
          onClose={() => {
            setShowLessonModal(false);
            setSelectedLesson(null);
          }}
          onCancel={() => {
            fetchCalendar();
            setShowLessonModal(false);
            setSelectedLesson(null);
          }}
        />
      )}
    </div>
  );
};

const LessonModal = ({ lesson, onClose, onCancel }) => {
  const [cancelling, setCancelling] = useState(false);

  const handleCancel = async () => {
    if (!window.confirm('Вы уверены, что хотите отменить этот урок?')) {
      return;
    }

    setCancelling(true);
    try {
      await api.patch(`/lessons/lessons/${lesson.id}/`, {
        status: 'cancelled',
      });
      alert('Урок отменен');
      onCancel();
    } catch (error) {
      console.error('Ошибка отмены урока:', error);
      alert('Ошибка при отмене урока');
    } finally {
      setCancelling(false);
    }
  };

  const formatDateTime = (dateStr, hour) => {
    const date = parseUTCDate(dateStr);
    date.setUTCHours(hour, 0, 0, 0);
    return formatUTCDateTime(date, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="lesson-modal-overlay" onClick={onClose}>
      <div className="lesson-modal" onClick={(e) => e.stopPropagation()}>
        <div className="lesson-modal-header">
          <h3>Информация об уроке</h3>
          <button type="button" className="lesson-modal-close" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="lesson-modal-content">
          <div className="lesson-info-item">
            <span className="lesson-info-label">Ученик:</span>
            <span className="lesson-info-value">{lesson.student_name || lesson.student_username}</span>
          </div>

          <div className="lesson-info-item">
            <span className="lesson-info-label">Дата и время:</span>
            <span className="lesson-info-value">
              {formatDateTime(lesson.date || new Date().toISOString(), lesson.hour)}
            </span>
          </div>

          <div className="lesson-info-item">
            <span className="lesson-info-label">Статус:</span>
            <span className="lesson-info-value">
              {lesson.status === 'scheduled'
                ? 'Запланирован'
                : lesson.status === 'completed'
                  ? 'Состоялся'
                  : lesson.status === 'cancelled'
                    ? 'Отменен'
                    : lesson.status}
            </span>
          </div>

          {lesson.duration_minutes && (
            <div className="lesson-info-item">
              <span className="lesson-info-label">Длительность:</span>
              <span className="lesson-info-value">{lesson.duration_minutes} минут</span>
            </div>
          )}
        </div>

        <div className="lesson-modal-actions">
          {lesson.status === 'scheduled' && (
            <button
              type="button"
              className="btn-cancel-lesson"
              onClick={handleCancel}
              disabled={cancelling}
            >
              {cancelling ? 'Отмена...' : 'Отменить урок'}
            </button>
          )}
          <button type="button" className="btn-close-modal" onClick={onClose}>
            Закрыть
          </button>
        </div>
      </div>
    </div>
  );
};

export default Schedule;
